/**
 * Les règles de l'import des résultats nationaux (plan « Nouvelle fenêtre d'import » du 30/10/2026, étape 2), sans
 * écran ni réseau. La fenêtre les enchaîne dans l'ordre du délégué :
 *   1. lireClasseur : un fichier déposé, avec le vrai nom de ses feuilles ;
 *   2. reconnaitreFichier, rangerFichiers : la restitution de l'établissement, un fichier de tests par classe et
 *      matière, et ce qui n'est ni l'un ni l'autre ;
 *   3. lignesDImport, etatDeLigne : une ligne par classe et matière, restitution et tests réunis élève par élève, et
 *      les résultats qui manqueront (lignes sans nom ni prénom, élèves incomplets) ;
 *   4. demandeDeVerification, lireVerification : ce que le serveur connaît de chaque élève ;
 *   5. nomsAConfirmer : « Même personne ? » et « Lequel ? », jamais tranchés d'avance ;
 *   6. envoiImport, lireResultat, messageDErreur : l'envoi des lignes prêtes et sa réponse.
 *
 * Le serveur (services/import_evalnat.py) complète une copie sans jamais l'écraser, cherche l'élève dans sa classe
 * puis dans le niveau, et compare les noms par une clé faite des seules lettres et chiffres : la même ici
 * (cleDuNom), pour réunir les deux fichiers d'un élève comme il les reconnaîtra.
 */
import * as XLSX from 'xlsx';
import { normalizeString } from './excelfunction';
import {
    findClasseKey, findNomPrenomKeys, lireColonneItem, validateQuestionsFile, validateScores, reponsesParItem,
    ajouterReponses,
} from './evalNatImportUtils';
import { EVAL_NAT_CONFIG } from '../components/VueEvalNat/EvalNat/evaluationConfig';

// ─── 1. Lire un fichier ───

const FORMATS = ['xlsx', 'xls', 'ods'];

/** Les feuilles d'un classeur lu par XLSX, dans l'ordre et avec leur nom : `[{ nom, lignes }]`. */
export const feuillesDuClasseur = (classeur) => classeur.SheetNames.map((nom) => ({
    nom,
    lignes: XLSX.utils.sheet_to_json(classeur.Sheets[nom], { defval: '' }),
}));

/** Un fichier déposé : `{ nom, feuilles }`. Refusé hors Excel et OpenDocument : le ministère exporte en .xlsx. */
export const lireClasseur = (fichier) => new Promise((resolve, reject) => {
    const extension = String(fichier?.name || '').toLowerCase().split('.').pop();
    if (!FORMATS.includes(extension)) {
        reject(new Error('Format non pris en charge : déposez les fichiers Excel (.xlsx) du ministère.'));
        return;
    }
    const lecteur = new FileReader();
    lecteur.onload = (e) => {
        try {
            resolve({ nom: fichier.name, feuilles: feuillesDuClasseur(XLSX.read(e.target.result, { type: 'array' })) });
        } catch {
            reject(new Error('Fichier illisible.'));
        }
    };
    lecteur.onerror = () => reject(new Error('Fichier illisible.'));
    lecteur.readAsArrayBuffer(fichier);
});

// ─── Noms des élèves et des classes ───

/**
 * Les mots d'un nom ou d'un prénom : sans accents, en majuscules, Œ et Æ dépliés ; tout ce qui n'est ni lettre ni
 * chiffre (tiret, apostrophe, espace) sépare. « Marie-Lou » → MARIE, LOU.
 */
export const motsDuNom = (texte) => String(texte ?? '')
    .normalize('NFKD').replace(/\p{M}/gu, '')
    .toUpperCase().replace(/Œ/g, 'OE').replace(/Æ/g, 'AE')
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);

/** La clé du serveur : les seules lettres et chiffres (« D'ALMEIDA » = « DALMEIDA », « LE GOFF » = « LEGOFF »). */
export const cleDuNom = (texte) => motsDuNom(texte).join('');

/** La clé d'un élève : son nom et son prénom. */
export const cleEleve = (nom, prenom) => `${cleDuNom(nom)}|${cleDuNom(prenom)}`;

/** La clé d'une question au délégué : un élève d'une classe, le même pour ses deux matières. */
export const cleQuestion = (classe, cle) => `${classe.id}|${cle}`;

/** Un nom de classe comparable : sans accents, espaces, tirets ni soulignés, en minuscules (« 6 AMBAVI » = « 6ambavi »). */
const classeComparable = (nom) => String(nom ?? '').toLowerCase().normalize('NFD')
    .replace(/[̀-ͯ]/g, '').replace(/[\s\-_]/g, '');

/** Une classe TanoEd telle que les lignes la gardent ; `nombreEleves` : ses élèves actifs, selon le serveur. */
const classeTanoEd = (c) => ({ id: c.id, nom: c.nom, nombreEleves: c.nombre_eleves });

/**
 * La classe TanoEd d'un nom de classe écrit dans un fichier : le même nom une fois comparable, sinon la seule classe
 * dont le nom commence ainsi (Excel tronque les noms longs) ; null si aucune ou plusieurs.
 */
export const trouverClasse = (nomFichier, classes) => {
    const cible = classeComparable(nomFichier);
    const liste = Array.isArray(classes) ? classes : [];
    if (!cible) return null;
    const exacte = liste.find((c) => classeComparable(c.nom) === cible);
    if (exacte) return classeTanoEd(exacte);
    const debut = liste.filter((c) => classeComparable(c.nom).startsWith(cible));
    return debut.length === 1 ? classeTanoEd(debut[0]) : null;
};

/** Le niveau d'un nom de classe, son premier chiffre (« 6 AMBAVI » → « 6 ») comme au serveur ; null sinon. */
export const niveauDeClasse = (nom) => String(nom ?? '').trim().match(/^(\d)/)?.[1] || null;

// ─── 2. Reconnaître un fichier ───

const configDe = (matiere, niveau) => EVAL_NAT_CONFIG[matiere === 'français' ? 'Français' : 'maths']?.[niveau] || null;

const enteteNormalise = (entete) => normalizeString(String(entete)).toLowerCase().trim();

const colonneCorrespond = (entete, colonne) => colonne.patterns.every((p) => entete.includes(p))
    && !(colonne.exclude || []).some((e) => (e instanceof RegExp ? e.test(entete) : entete.includes(e)));

/** Les colonnes de la restitution d'une feuille : `{ [colonne de la configuration]: en-tête du fichier }`. */
const colonnesRestitution = (entetes, config) => {
    const colonnes = {};
    entetes.forEach((entete) => {
        const normalise = enteteNormalise(entete);
        Object.entries(config.columns).forEach(([id, colonne]) => {
            if (colonneCorrespond(normalise, colonne)) colonnes[id] = entete;
        });
    });
    return colonnes;
};

/** La matière d'une feuille de restitution, d'après ses en-têtes et la configuration du niveau ; null si aucune. */
const matiereRestitution = (entetes, niveau) => {
    const tous = entetes.map(enteteNormalise).join(' ');
    const reconnue = (config) => Object.values(config?.columns || {})
        .some((c) => c.patterns.length > 0 && c.patterns.every((p) => tous.includes(p)));
    if (reconnue(configDe('maths', niveau))) return 'maths';
    if (reconnue(configDe('français', niveau))) return 'français';
    return null;
};

/**
 * Les feuilles de résultats d'une restitution. Une feuille sans colonne Classe (résumé de l'exportation, notes de
 * lecture) ou sans matière reconnue n'en est pas une : elle est ignorée sans être listée. Une feuille de résultats
 * qui ne se lit pas est listée avec sa raison.
 */
const restitutionDe = (feuilles) => {
    const blocs = [];
    const feuillesEnEchec = [];
    feuilles.forEach(({ nom: feuille, lignes }) => {
        const entetes = Object.keys(lignes[0]);
        const classe = findClasseKey(entetes);
        if (!classe) return;
        const niveau = niveauDeClasse(lignes.map((l) => l[classe]).find((v) => String(v ?? '').trim()));
        const matiere = niveau ? matiereRestitution(entetes, niveau) : null;
        if (!matiere) return;
        const { nomKey, prenomKey } = findNomPrenomKeys(entetes);
        const config = configDe(matiere, niveau);
        const colonnes = colonnesRestitution(entetes, config);
        const manquantes = Object.keys(config.columns).filter((id) => !colonnes[id]).map((id) => config.columns[id].displayName);
        if (!nomKey || !prenomKey) {
            feuillesEnEchec.push({ feuille, raison: 'colonne Nom ou Prénom introuvable' });
        } else if (manquantes.length > 0) {
            feuillesEnEchec.push({ feuille, raison: `colonnes introuvables : ${manquantes.join(', ')}` });
        } else {
            blocs.push({ feuille, niveau, matiere, cles: { classe, nom: nomKey, prenom: prenomKey }, colonnes, lignes });
        }
    });
    return { blocs, feuillesEnEchec };
};

/** La matière d'un fichier de tests : les maths ont des automatismes ou des problèmes, le reste est du français. */
const matiereDesTests = (entetes) => {
    const tous = entetes.map(enteteNormalise).join(' ');
    return tous.includes('automatisme') || tous.includes('probleme') ? 'maths' : 'français';
};

/**
 * Un fichier de tests : sa classe (colonne Classe), sa matière, ses feuilles de tests passées aux contrôles d'usage,
 * avec leur vrai nom (`nomsDesFeuilles`, pour dire où est une ligne sans nom). Deux feuilles qui ne listent pas les
 * mêmes élèves ne refusent plus le fichier (31/10/2026) : chacun garde ce qu'il a, et le manque est dit sur la ligne.
 */
const fichierDeTests = (nom, feuilles, classes) => {
    const reponse = {
        type: 'tests', nom, classeFichier: null, classe: null, niveau: null, matiere: null, feuilles: [], nomsDesFeuilles: [],
        columnMaps: [], erreurs: [],
    };
    const lignes = feuilles[0].lignes;
    const cleClasse = findClasseKey(Object.keys(lignes[0]));
    if (!cleClasse) return { ...reponse, erreurs: ['Colonne « Classe » introuvable.'] };
    const valeurs = [...new Set(lignes.map((l) => String(l[cleClasse] ?? '').trim()).filter(Boolean))];
    if (valeurs.length !== 1) {
        return { ...reponse, erreurs: [valeurs.length === 0 ? 'Aucune classe indiquée.' : `Plusieurs classes dans le même fichier : ${valeurs.join(', ')}.`] };
    }
    const classeFichier = valeurs[0];
    const niveau = niveauDeClasse(classeFichier);
    const matiere = matiereDesTests(Object.keys(lignes[0]));
    const reconnu = { ...reponse, classeFichier, niveau, matiere, classe: trouverClasse(classeFichier, classes) };
    const config = configDe(matiere, niveau);
    if (!config) return { ...reconnu, erreurs: [`Pas d'évaluation nationale de ${matiere} pour la classe ${classeFichier}.`] };
    const { errors, columnMaps, sheets } = validateQuestionsFile(feuilles.map((f) => f.lignes), config, classeFichier);
    const nomsDesFeuilles = sheets.map((s, i) => feuilles.find((f) => f.lignes === s)?.nom || `Feuille ${i + 1}`);
    const erreurs = errors.length > 0 ? errors : validateScores(sheets, columnMaps);
    return { ...reconnu, feuilles: sheets, nomsDesFeuilles, columnMaps, erreurs };
};

const RESTITUTION_ANONYME = "c'est la restitution anonyme, sans aucun nom d'élève. Déposez la version nominative.";

/** Une feuille de résultats dont au moins une ligne porte un nom et un prénom. */
const aDesNoms = (bloc) => bloc.lignes.some((l) => String(l[bloc.cles.nom] ?? '').trim() && String(l[bloc.cles.prenom] ?? '').trim());

/**
 * Ce qu'est un fichier déposé, d'après son contenu (son nom ne compte pas) :
 *   - `{ type: 'tests', nom, classeFichier, classe, niveau, matiere, feuilles, nomsDesFeuilles, columnMaps, erreurs }` :
 *     les feuilles de tests d'une classe ; `classe` est la classe TanoEd (null si inconnue) ; `erreurs` non vide =
 *     fichier refusé ;
 *   - `{ type: 'restitution', nom, blocs, feuillesEnEchec }` : un bloc par feuille de résultats (niveau, matière) ;
 *   - `{ type: 'inconnu', nom, raison }` : un fichier qui ne sert pas, avec sa raison ; la restitution anonyme en est
 *     un (n° 28 du diagnostic du 31/10/2026) : aucune de ses lignes n'a de nom, rien ne dirait à qui sont ses résultats.
 */
export const reconnaitreFichier = (fichier, classes) => {
    const feuilles = (fichier?.feuilles || []).filter((f) => Array.isArray(f.lignes) && f.lignes.length > 0);
    const deTests = feuilles.filter((f) => Object.keys(f.lignes[0]).some((entete) => lireColonneItem(entete)));
    if (deTests.length > 0) return fichierDeTests(fichier.nom, deTests, classes);
    const restitution = restitutionDe(feuilles);
    if (restitution.blocs.length > 0 && !restitution.blocs.some(aDesNoms)) {
        return { type: 'inconnu', nom: fichier.nom, raison: RESTITUTION_ANONYME };
    }
    if (restitution.blocs.length + restitution.feuillesEnEchec.length > 0) {
        return { type: 'restitution', nom: fichier.nom, ...restitution };
    }
    return { type: 'inconnu', nom: fichier?.nom, raison: "Ni la restitution ni un fichier de tests : aucune colonne des évaluations nationales." };
};

const cleDeLigne = (classe, classeFichier, matiere) => `${classe ? classe.id : `?${classeComparable(classeFichier)}`}|${matiere}`;

/**
 * Les fichiers reconnus, dans l'ordre du dépôt, rangés : la restitution, un fichier de tests par classe et matière
 * (le dernier déposé l'emporte ; `remplaces` dit lequel il remplace, sauf si c'est le même fichier redéposé), et les
 * `refuses` : fichier de tests en erreur, restitution sans aucune feuille lisible ou anonyme, fichier inconnu.
 */
export const rangerFichiers = (fichiers) => {
    let restitution = null;
    const tests = new Map();
    const remplaces = [];
    const refuses = [];
    const garder = (ancien, nouveau) => {
        if (ancien && ancien.nom !== nouveau.nom) remplaces.push({ ancien: ancien.nom, nouveau: nouveau.nom });
    };
    (fichiers || []).forEach((fichier) => {
        if (fichier.type === 'restitution' && fichier.blocs.length > 0) {
            garder(restitution, fichier);
            restitution = fichier;
        } else if (fichier.type === 'tests' && fichier.erreurs.length === 0) {
            const cle = cleDeLigne(fichier.classe, fichier.classeFichier, fichier.matiere);
            garder(tests.get(cle), fichier);
            tests.set(cle, fichier);
        } else {
            refuses.push(fichier);
        }
    });
    return { restitution, tests: [...tests.values()], remplaces, refuses };
};

// ─── 3. Une ligne par classe et matière ───

/** Les réponses d'un élève dans la restitution : `{ [nom de sortie de la configuration]: niveau du ministère }`. */
const reponsesRestitution = (ligne, bloc) => {
    const config = configDe(bloc.matiere, bloc.niveau);
    const reponses = {};
    Object.entries(bloc.colonnes).forEach(([id, entete]) => {
        const valeur = ligne[entete];
        if (valeur !== undefined && valeur !== null && valeur !== '') reponses[config.columns[id].outputName] = valeur;
    });
    return reponses;
};

/** Le numéro d'une ligne dans le tableur, en-tête en ligne 1 (sheet_to_json saute les lignes vides : l'index ne suffit pas). */
const numeroDeLigne = (rang, index) => (Number.isInteger(rang?.__rowNum__) ? rang.__rowNum__ + 1 : index + 2);

/**
 * Ajoute à une ligne les élèves d'une feuille, `entrees` : `[{ rang, numero }]`, `fichier` : « restitution » ou
 * « tests ». Deux élèves de même clé dans une feuille sont mis à part (`doublons`) : rien ne dit à qui sont leurs
 * résultats. Une ligne sans nom ou sans prénom n'est jamais rangée : elle est notée là où elle est (`sansNom`).
 * `presences` retient d'où vient chaque élève (« restitution » ou le nom de la feuille), pour dire ce qui lui manquera.
 */
const ajouterFeuille = (ligne, entrees, cles, ranger, { fichier, feuille }) => {
    const source = fichier === 'restitution' ? fichier : feuille;
    const eleves = entrees.map(({ rang, numero }) => ({
        nom: String(rang[cles.nom] ?? '').trim(), prenom: String(rang[cles.prenom] ?? '').trim(), rang, numero,
    }));
    const vus = new Map();
    eleves.forEach((e) => {
        if (!e.nom || !e.prenom) return;
        const cle = cleEleve(e.nom, e.prenom);
        vus.set(cle, (vus.get(cle) || 0) + 1);
    });
    eleves.forEach(({ nom, prenom, rang, numero }) => {
        if (!nom || !prenom) {
            ligne.sansNom.push({ fichier, feuille, numero });
            return;
        }
        const cle = cleEleve(nom, prenom);
        if (vus.get(cle) > 1) {
            if (!ligne.doublons.has(cle)) ligne.doublons.set(cle, { nom, prenom });
            return;
        }
        const eleve = ligne.eleves.get(cle) || { cle, nom, prenom, reponses: {} };
        ranger(eleve.reponses, rang);
        ligne.eleves.set(cle, eleve);
        ligne.presences.set(cle, (ligne.presences.get(cle) || new Set()).add(source));
    });
};

/**
 * Ce qui manquera à un élève d'une ligne qui a ses deux fichiers : « la restitution », « le fichier de tests »,
 * « la feuille « Automatismes » », ou deux de ces manques ; null s'il ne lui manque rien.
 */
const ceQuiManque = (ligne, presences = new Set()) => {
    const manques = [];
    if (!presences.has('restitution')) manques.push('la restitution');
    const absentes = ligne.feuillesDeTests.filter((f) => !presences.has(f));
    if (absentes.length > 0 && absentes.length === ligne.feuillesDeTests.length) manques.push('le fichier de tests');
    else if (absentes.length > 0) manques.push(absentes.map((f) => `la feuille « ${f} »`).join(' et '));
    return manques.length > 0 ? manques.join(' et ') : null;
};

/** Le plus grand nombre de lignes sans nom d'une même feuille : un élève sans nom dans trois feuilles compte une fois. */
const plusDeLignesSansNom = (sansNom) => {
    const parFeuille = new Map();
    sansNom.forEach(({ fichier, feuille }) => {
        const cle = `${fichier}|${feuille}`;
        parFeuille.set(cle, (parFeuille.get(cle) || 0) + 1);
    });
    return Math.max(0, ...parFeuille.values());
};

/**
 * Une ligne par classe et matière, à partir des fichiers rangés : `{ cle, classe, classeFichier, niveau, matiere,
 * restitution, fichierTests, feuillesDeTests, eleves, doublons, sansNom, incomplets, manquants }`. Un élève y réunit
 * sa ligne de la restitution et celles de ses tests, par la clé du serveur (le ministère l'écrit pareil dans ses
 * fichiers) ; un élève absent d'un des fichiers garde ce qu'il a. `classe` est null pour une classe inconnue de TanoEd.
 * Ce qui manquera (31/10/2026, une seule pastille dans la fenêtre, le détail au clic) :
 *   - sansNom : les lignes sans nom ni prénom, jamais importées, `[{ fichier, feuille, numero }]` ;
 *   - incomplets : pour une ligne qui a ses deux fichiers, les élèves à qui il manque la restitution, le fichier de
 *     tests ou une feuille, `[{ cle, nom, prenom, manque }]` ;
 *   - manquants : le nombre de la pastille, 0 sans les deux fichiers.
 */
export const lignesDImport = ({ restitution, tests } = {}, classes) => {
    const lignes = new Map();
    const ligneDe = (classeFichier, niveau, matiere) => {
        const classe = trouverClasse(classeFichier, classes);
        const cle = cleDeLigne(classe, classeFichier, matiere);
        if (!lignes.has(cle)) {
            lignes.set(cle, {
                cle, classe, classeFichier, niveau: classe ? niveauDeClasse(classe.nom) : niveau, matiere,
                restitution: false, fichierTests: null, feuillesDeTests: [], eleves: new Map(), doublons: new Map(),
                presences: new Map(), sansNom: [],
            });
        }
        return lignes.get(cle);
    };

    (restitution?.blocs || []).forEach((bloc) => {
        const parClasse = new Map();
        bloc.lignes.forEach((rang, i) => {
            const classeFichier = String(rang[bloc.cles.classe] ?? '').trim();
            if (classeFichier) parClasse.set(classeFichier, [...(parClasse.get(classeFichier) || []), { rang, numero: numeroDeLigne(rang, i) }]);
        });
        parClasse.forEach((entrees, classeFichier) => {
            const ligne = ligneDe(classeFichier, bloc.niveau, bloc.matiere);
            ligne.restitution = true;
            ajouterFeuille(ligne, entrees, bloc.cles, (reponses, rang) => Object.assign(reponses, reponsesRestitution(rang, bloc)),
                { fichier: 'restitution', feuille: bloc.feuille });
        });
    });

    (tests || []).forEach((fichier) => {
        const ligne = ligneDe(fichier.classeFichier, fichier.niveau, fichier.matiere);
        ligne.fichierTests = fichier.nom;
        ligne.feuillesDeTests = fichier.feuilles.map((_, i) => fichier.nomsDesFeuilles?.[i] || `Feuille ${i + 1}`);
        fichier.feuilles.forEach((feuille, i) => {
            const columnMap = fichier.columnMaps[i];
            const entrees = feuille.map((rang, j) => ({ rang, numero: numeroDeLigne(rang, j) }));
            ajouterFeuille(ligne, entrees, columnMap, (reponses, rang) => ajouterReponses(reponses, reponsesParItem(rang, columnMap)),
                { fichier: 'tests', feuille: ligne.feuillesDeTests[i] });
        });
    });

    return [...lignes.values()]
        .map(({ presences, ...ligne }) => {
            ligne.doublons.forEach((_, cle) => ligne.eleves.delete(cle));
            const eleves = [...ligne.eleves.values()];
            const deuxFichiers = ligne.restitution && Boolean(ligne.fichierTests);
            const incomplets = deuxFichiers ? eleves.flatMap((e) => {
                const manque = ceQuiManque(ligne, presences.get(e.cle));
                return manque ? [{ cle: e.cle, nom: e.nom, prenom: e.prenom, manque }] : [];
            }) : [];
            // L'élève sans nom d'un fichier est presque toujours celui à qui ce fichier manque : le plus grand des deux
            // comptes, pas leur somme, pour ne pas le compter deux fois.
            const manquants = deuxFichiers ? Math.max(incomplets.length, plusDeLignesSansNom(ligne.sansNom)) : 0;
            return { ...ligne, eleves, doublons: [...ligne.doublons.values()], incomplets, manquants };
        })
        .sort((a, b) => String(a.classe?.nom ?? a.classeFichier).localeCompare(String(b.classe?.nom ?? b.classeFichier), 'fr', { numeric: true })
            || a.matiere.localeCompare(b.matiere, 'fr'));
};

/**
 * L'état d'une ligne : « prete » (ses deux fichiers sont là), « sans_tests », « sans_restitution »,
 * « classe_inconnue » (pas dans TanoEd), « classe_vide » (dans TanoEd sans aucun élève : rien ne peut s'y ranger,
 * avec ou sans fichier de tests, 01/11/2026), ou « refusee » quand la vérification du serveur l'a refusée.
 */
export const etatDeLigne = (ligne, verification) => {
    if (!ligne.classe) return 'classe_inconnue';
    if (ligne.classe.nombreEleves === 0) return 'classe_vide';
    if (verification?.parLigne?.get(ligne.cle)?.erreur) return 'refusee';
    if (!ligne.fichierTests) return 'sans_tests';
    if (!ligne.restitution) return 'sans_restitution';
    return 'prete';
};

/** Les classes des fichiers absentes de TanoEd, chacune une fois pour ses deux matières, dans l'ordre des lignes. */
export const classesHorsTanoEd = (lignes) => [...new Set((lignes || [])
    .filter((l) => !l.classe).map((l) => l.classeFichier))];

// ─── 4. La vérification ───

/**
 * La demande au serveur (`eleves/check-missing-for-eval-nat/`) : les élèves de chaque ligne dont la classe est connue
 * et a des élèves ; une classe vide n'a rien à vérifier.
 */
export const demandeDeVerification = (lignes, etablissementId) => {
    const classes = new Map();
    (lignes || []).forEach((ligne) => {
        if (!ligne.classe || etatDeLigne(ligne) === 'classe_vide' || ligne.eleves.length === 0) return;
        const entree = classes.get(ligne.classe.id) || { classe_id: ligne.classe.id, classe_nom: ligne.classe.nom, matieres: {} };
        entree.matieres[ligne.matiere] = ligne.eleves.map(({ nom, prenom }) => ({ nom, prenom }));
        classes.set(ligne.classe.id, entree);
    });
    return classes.size > 0 ? { etablissement_id: Number(etablissementId), classes: [...classes.values()] } : null;
};

const verificationDeLigne = (resultat) => {
    const manquants = resultat.manquants || [];
    const fiches = (m) => m.trouve_dans || [];
    const ailleurs = manquants.filter((m) => fiches(m).length === 1).map((m) => ({ nom: m.nom, prenom: m.prenom, fiche: fiches(m)[0] }));
    const aChoisir = manquants.filter((m) => fiches(m).length > 1)
        .map((m) => ({ nom: m.nom, prenom: m.prenom, candidats: fiches(m) }));
    const partis = manquants.filter((m) => m.parti).map(({ nom, prenom }) => ({ nom, prenom }));
    const inconnus = manquants.filter((m) => fiches(m).length === 0 && !m.parti).map(({ nom, prenom }) => ({ nom, prenom }));
    const presents = resultat.presents || 0;
    const dejaImportes = resultat.deja_importes || 0;
    return { presents, dejaImportes, aCreer: Math.max(0, presents - dejaImportes), ailleurs, aChoisir, inconnus, partis, erreur: null };
};

/**
 * La réponse de la vérification : `parLigne` (clé de ligne → `{ presents, dejaImportes, aCreer, ailleurs, aChoisir,
 * inconnus, partis, erreur }`) et `sansResultats`, les fiches du niveau sans copie ni ligne dans les fichiers.
 *   - aCreer : élèves reconnus qui n'ont pas encore de copie ;
 *   - ailleurs : une seule fiche à ce nom, dans une autre classe du niveau (`fiche` : `{ eleve_id, classe_id,
 *     classe_nom, nom, prenom, a_des_resultats }`) ; le serveur ne la prend que sur le « Oui » du délégué (« quand
 *     il y a un doute, c'est le prof qui confirme », 31/10/2026) ;
 *   - aChoisir : plusieurs fiches à ce nom : le serveur ne tranche pas, le délégué choisit ;
 *   - inconnus : aucune fiche à ce nom ;
 *   - partis : seule une fiche désactivée porte ce nom, l'élève a quitté l'établissement (n° 33).
 */
export const lireVerification = (reponse, lignes) => {
    const parLigne = new Map();
    (reponse?.resultats || []).forEach((resultat) => {
        (lignes || []).filter((l) => l.classe && l.classe.id === resultat.classe_id).forEach((ligne) => {
            if (resultat.error) {
                parLigne.set(ligne.cle, { erreur: resultat.error });
                return;
            }
            const matiere = resultat.matieres?.[ligne.matiere];
            if (matiere) parLigne.set(ligne.cle, verificationDeLigne(matiere));
        });
    });
    return { parLigne, sansResultats: reponse?.sans_resultats || [] };
};

// ─── 5. Ce que le délégué tranche ───

const inclus = (petits, grands) => petits.length > 0 && petits.every((mot) => grands.includes(mot));
const lUnDansLAutre = (a, b) => inclus(a, b) || inclus(b, a);

/**
 * « Même personne ? » se pose quand le nom et le prénom se contiennent l'un l'autre, mot par mot : un mot de plus ou
 * de moins (« MARTIN Lou » et « MARTIN DURAND Lou »). Jamais pour des lettres en plus (« Lou » et « Louna »,
 * « MARTIN » et « MARTINEZ »), ni pour deux noms identiques une fois normalisés : dans la classe du fichier le serveur
 * les rapproche seul, dans une autre classe la vérification les donne déjà (`ailleurs`).
 */
export const memePersonnePossible = (a, b) => cleEleve(a.nom, a.prenom) !== cleEleve(b.nom, b.prenom)
    && lUnDansLAutre(motsDuNom(a.nom), motsDuNom(b.nom))
    && lUnDansLAutre(motsDuNom(a.prenom), motsDuNom(b.prenom));

/** Les fiches à proposer pour un élève inconnu : sans résultats, du même niveau, la classe du fichier d'abord. */
const propositionsPour = (question, sansResultats, prises) => {
    const vues = new Set();
    return (sansResultats || [])
        .filter((f) => f.niveau === question.niveau && question.matieres.includes(f.matiere)
            && !prises.has(f.eleve_id) && memePersonnePossible(question, f))
        .filter((f) => {
            if (vues.has(f.eleve_id)) return false;
            vues.add(f.eleve_id);
            return true;
        })
        .map(({ eleve_id, nom, prenom, classe_id, classe_nom }) => ({ eleve_id, nom, prenom, classe_id, classe_nom }))
        .sort((a, b) => (b.classe_id === question.classe.id) - (a.classe_id === question.classe.id)
            || String(a.classe_nom).localeCompare(String(b.classe_nom), 'fr', { numeric: true })
            || `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, 'fr'));
};

/**
 * Les questions au délégué, une par élève et par classe (la même pour ses deux matières : le ministère écrit son nom
 * pareil dans tous ses fichiers) :
 *   - `{ type: 'meme_personne', cle, nom, prenom, classe, niveau, matieres, propositions }` : un élève que le serveur
 *     ne range pas seul, et les fiches qui pourraient être la sienne : la fiche du même nom trouvée dans une autre
 *     classe, ou, pour un inconnu, une fiche sans résultats au nom à un mot près. Un inconnu sans proposition n'est
 *     pas une question ;
 *   - `{ type: 'lequel', ..., candidats }` : plusieurs fiches portent ce nom.
 * `choix` (clé de question → numéro de fiche) : une fiche choisie pour une question n'est plus proposée ailleurs.
 */
export const nomsAConfirmer = (lignes, verification, choix = {}) => {
    const questions = new Map();
    (lignes || []).forEach((ligne) => {
        const verifie = verification?.parLigne?.get(ligne.cle);
        if (!ligne.classe || !verifie || verifie.erreur) return;
        const poser = (type, eleve, details) => {
            const cle = cleQuestion(ligne.classe, cleEleve(eleve.nom, eleve.prenom));
            const question = questions.get(cle) || {
                type, cle, nom: eleve.nom, prenom: eleve.prenom, classe: ligne.classe, niveau: ligne.niveau, matieres: [],
                trouvees: new Map(), ...details,
            };
            if (!question.matieres.includes(ligne.matiere)) question.matieres.push(ligne.matiere);
            questions.set(cle, question);
            return question;
        };
        verifie.inconnus.forEach((eleve) => poser('meme_personne', eleve, {}));
        verifie.ailleurs.forEach(({ fiche, ...eleve }) => poser('meme_personne', eleve, {}).trouvees.set(fiche.eleve_id, fiche));
        verifie.aChoisir.forEach((eleve) => poser('lequel', eleve, { candidats: eleve.candidats }));
    });

    return [...questions.values()].flatMap(({ trouvees, ...question }) => {
        const prises = new Set(Object.entries(choix || {})
            .filter(([cle, id]) => cle !== question.cle && id)
            .map(([, id]) => Number(id)));
        if (question.type === 'lequel') {
            const candidats = question.candidats.filter((c) => !prises.has(c.eleve_id));
            return candidats.length > 0 ? [{ ...question, candidats }] : [];
        }
        const propositions = [
            ...[...trouvees.values()].map((f) => ({
                eleve_id: f.eleve_id, nom: f.nom || question.nom, prenom: f.prenom || question.prenom, classe_id: f.classe_id, classe_nom: f.classe_nom,
            })),
            ...propositionsPour(question, verification.sansResultats, prises),
        ].filter((f, i, toutes) => !prises.has(f.eleve_id) && toutes.findIndex((g) => g.eleve_id === f.eleve_id) === i);
        return propositions.length > 0 ? [{ ...question, propositions }] : [];
    });
};

// ─── 6. L'envoi et sa réponse ───

/** La date du jour, « 2026-10-30 » : la campagne la prend à sa création (l'import ne connaît pas la date de passation). */
export const dateDuJour = (jour = new Date()) =>
    `${jour.getFullYear()}-${String(jour.getMonth() + 1).padStart(2, '0')}-${String(jour.getDate()).padStart(2, '0')}`;

/**
 * Le corps de l'import (`evaluations_nat/create_national_evaluation/`) : les lignes prêtes, toutes, même déjà
 * importées, puisque le serveur complète ce qui manque ou répond « déjà importé » et que la vérification ne dit pas
 * si une copie est complète. Chaque élève part avec son nom écrit par le ministère, et avec la fiche que le délégué a
 * désignée (`eleve_id`) seulement s'il l'a fait. Null s'il n'y a rien à envoyer.
 */
export const envoiImport = (lignes, verification, choix, etablissementId, date = dateDuJour()) => {
    const pretes = (lignes || []).filter((l) => etatDeLigne(l, verification) === 'prete' && l.eleves.length > 0);
    if (pretes.length === 0) return null;
    return {
        etablissement_id: Number(etablissementId),
        classes: pretes.map((ligne) => ({
            classe_id: ligne.classe.id,
            classe_nom: ligne.classe.nom,
            matiere: ligne.matiere,
            date,
            eleves: ligne.eleves.map((eleve) => {
                const eleveId = choix?.[cleQuestion(ligne.classe, eleve.cle)];
                return { Nom: eleve.nom, Prénom: eleve.prenom, ...(eleveId ? { eleve_id: eleveId } : {}), reponses: eleve.reponses };
            }),
        })),
    };
};

/**
 * La réponse de l'import, ligne par ligne. Le serveur rend un résultat par bloc envoyé, dans l'ordre, et dans chaque
 * bloc un statut par élève, dans l'ordre aussi : c'est ce qui relie un élève à la fiche désignée (`confirmes`).
 * `{ phrase, lignes: [{ classe, matiere, statut, crees, completes, dejaImportes, confirmes, affichesAilleurs,
 * inconnus, ambigus, partis, message }] }` ; `statut` : « success », « partial » (des inconnus ou des ambigus),
 * « error ». Un élève « parti » n'a plus qu'une fiche désactivée : il a quitté l'établissement, rien n'est importé
 * (n° 33 du diagnostic du 31/10/2026).
 */
const ECRITS = new Set(['cree', 'complete', 'deja_importe']);

export const lireResultat = (reponse, envoi) => ({
    phrase: reponse?.message || '',
    lignes: (reponse?.details || []).map((bloc, i) => {
        const envoyes = envoi?.classes?.[i]?.eleves || [];
        const eleves = bloc.eleves || [];
        return {
            classe: bloc.classe,
            matiere: bloc.matiere,
            statut: bloc.status,
            crees: bloc.crees || 0,
            completes: bloc.completes || 0,
            dejaImportes: bloc.deja_importes || 0,
            confirmes: eleves.filter((e, j) => envoyes[j]?.eleve_id && ECRITS.has(e.statut)).length,
            affichesAilleurs: eleves.filter((e) => e.classe_trouvee).map((e) => ({ nom: e.nom, prenom: e.prenom, classe: e.classe_trouvee })),
            inconnus: eleves.filter((e) => e.statut === 'inconnu').map(({ nom, prenom }) => ({ nom, prenom })),
            ambigus: eleves.filter((e) => e.statut === 'ambigu').map(({ nom, prenom, candidats }) => ({ nom, prenom, candidats: candidats || [] })),
            partis: eleves.filter((e) => e.statut === 'parti').map(({ nom, prenom }) => ({ nom, prenom })),
            message: bloc.message || null,
        };
    }),
});

/**
 * Ce qu'on dit quand la vérification ou l'import n'aboutit pas (`resultat` : le retour de usePostApi). Après un délai
 * dépassé, redéposer ne casse rien : ce qui est déjà importé sort « déjà importé ».
 */
export const messageDErreur = (resultat, operation = 'import') => {
    const erreur = resultat?.error;
    const texte = erreur?.response?.data?.error;
    if (erreur?.response?.status === 504 || erreur?.code === 'ECONNABORTED' || /timeout/i.test(erreur?.message || '')) {
        return operation === 'import'
            ? "Le serveur n'a pas répondu à temps. Redéposez les mêmes fichiers : ce qui est déjà importé sortira « déjà importé »."
            : "Le serveur n'a pas répondu à temps. Réessayez.";
    }
    if (typeof texte === 'string' && texte) return texte;
    if (!erreur?.response) return 'Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.';
    return operation === 'import' ? "L'import n'a pas abouti. Réessayez." : "La vérification n'a pas abouti. Réessayez.";
};
